// @ts-strict-ignore
import React, { useState } from 'react'
import { TFunction } from 'i18next'
import {
  Button,
  Box as Column,
  ComboboxGroup,
  Icon,
  Input,
  Box as OuterWrapper,
  type ComboboxGroupItem,
} from '@node-space/ui-components'
import { PaymentSearchType } from 'constants/General'
import { type PaymentSearchTypeValue } from 'types/payments'

interface SearchFieldsProps {
  searchByRef: string
  searchByTransactionHash: string
  searchByUUID: string
  t: TFunction
  onSearchByRefChanged: (value: string) => void
  onSearchByTransactionHash: (value: string) => void
  onSearchByUUID: (value: string) => void
}

const SearchFields = ({
  searchByRef,
  searchByTransactionHash,
  searchByUUID,
  t,
  onSearchByRefChanged,
  onSearchByTransactionHash,
  onSearchByUUID,
}: SearchFieldsProps) => {
  const getInitialSearchType = () => {
    if (searchByTransactionHash) return PaymentSearchType.HASH
    if (searchByUUID) return PaymentSearchType.UUID
    return PaymentSearchType.REFERENCE
  }

  const [searchType, setSearchType] = useState<PaymentSearchTypeValue>(getInitialSearchType)

  const searchTypeItems: ComboboxGroupItem[] = [
    {
      groupLabelHeader: t('payments.searchType'),
      items: [
        { label: t('payments.searchTypeReference'), value: PaymentSearchType.REFERENCE },
        { label: t('payments.searchTypeHash'), value: PaymentSearchType.HASH },
        { label: t('payments.searchTypeUUID'), value: PaymentSearchType.UUID },
      ],
    },
  ]

  const searchValueMap = {
    [PaymentSearchType.REFERENCE]: searchByRef,
    [PaymentSearchType.HASH]: searchByTransactionHash,
    [PaymentSearchType.UUID]: searchByUUID,
  }

  const placeholderMap = {
    [PaymentSearchType.REFERENCE]: t('payments.searchByReference'),
    [PaymentSearchType.HASH]: t('payments.searchByHash'),
    [PaymentSearchType.UUID]: t('payments.searchByUUID'),
  }

  const currentValue = searchValueMap[searchType]
  const placeholderText = placeholderMap[searchType]

  const handleChange = (value: string) => {
    switch (searchType) {
      case PaymentSearchType.REFERENCE:
        onSearchByRefChanged(value)
        break
      case PaymentSearchType.HASH:
        onSearchByTransactionHash(value)
        break
      case PaymentSearchType.UUID:
        onSearchByUUID(value)
        break
    }
  }

  const handleClear = () => {
    onSearchByRefChanged('')
    onSearchByTransactionHash('')
    onSearchByUUID('')
  }

  const handleSearchTypeChange = (value: string) => {
    handleClear()
    setSearchType(value as PaymentSearchTypeValue)
  }

  return (
    <Column className="flex w-full mr-3 gap-2">
      <OuterWrapper className="w-40 shrink-0 [&>div>div:first-child]:hidden">
        <ComboboxGroup
          id="payment-search-type"
          testId="payment-search-type"
          items={searchTypeItems}
          defaultValue={searchType}
          onChange={handleSearchTypeChange}
          filter={false}
        />
      </OuterWrapper>
      <OuterWrapper testId="filter-search-field-wrapper" flexSize="auto">
        <Input
          placeholderText={placeholderText}
          testId="filter-search-field"
          onChange={e => handleChange(e?.target.value)}
          searchable
          value={currentValue}
          iconPosition="right"
          IconButton={
            currentValue && (
              <Button variant="ghost" onClick={handleClear} iconElement={<Icon name="X" />} />
            )
          }
        />
      </OuterWrapper>
    </Column>
  )
}

export default SearchFields
